import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as paymentMethodService from '../../services/paymentMethod';

// Async thunks
export const fetchPaymentMethods = createAsyncThunk(
  'paymentMethod/fetchPaymentMethods',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await paymentMethodService.getPaymentMethods(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPaymentMethod = createAsyncThunk(
  'paymentMethod/fetchPaymentMethod',
  async (id, { rejectWithValue }) => {
    try {
      const response = await paymentMethodService.getPaymentMethod(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createPaymentMethod = createAsyncThunk(
  'paymentMethod/createPaymentMethod',
  async (paymentMethodData, { rejectWithValue }) => {
    try {
      const response = await paymentMethodService.createPaymentMethod(paymentMethodData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updatePaymentMethod = createAsyncThunk(
  'paymentMethod/updatePaymentMethod',
  async ({ id, paymentMethodData }, { rejectWithValue }) => {
    try {
      const response = await paymentMethodService.updatePaymentMethod(id, paymentMethodData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deletePaymentMethod = createAsyncThunk(
  'paymentMethod/deletePaymentMethod',
  async (id, { rejectWithValue }) => {
    try {
      const response = await paymentMethodService.deletePaymentMethod(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  paymentMethods: [],
  currentPaymentMethod: null,
  loading: false,
  error: null,
  totalPaymentMethods: 0,
};

const paymentMethodSlice = createSlice({
  name: 'paymentMethod',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentPaymentMethod: (state) => {
      state.currentPaymentMethod = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Payment Methods
      .addCase(fetchPaymentMethods.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentMethods.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentMethods = action.payload.results || action.payload.payment_methods || action.payload;
        state.totalPaymentMethods = action.payload.count || action.payload.length || 0;
      })
      .addCase(fetchPaymentMethods.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Single Payment Method
      .addCase(fetchPaymentMethod.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentMethod.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPaymentMethod = action.payload;
      })
      .addCase(fetchPaymentMethod.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create Payment Method
      .addCase(createPaymentMethod.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPaymentMethod.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentMethods.push(action.payload);
        state.totalPaymentMethods += 1;
      })
      .addCase(createPaymentMethod.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update Payment Method
      .addCase(updatePaymentMethod.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePaymentMethod.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.paymentMethods.findIndex(pm => pm.id === action.payload.id);
        if (index !== -1) {
          state.paymentMethods[index] = action.payload;
        }
        if (state.currentPaymentMethod && state.currentPaymentMethod.id === action.payload.id) {
          state.currentPaymentMethod = action.payload;
        }
      })
      .addCase(updatePaymentMethod.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete Payment Method
      .addCase(deletePaymentMethod.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePaymentMethod.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentMethods = state.paymentMethods.filter(pm => pm.id !== action.meta.arg);
        state.totalPaymentMethods -= 1;
      })
      .addCase(deletePaymentMethod.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearCurrentPaymentMethod } = paymentMethodSlice.actions;
export default paymentMethodSlice.reducer;
